/**
 * Currency Selector Component
 * Dropdown to select preferred currency
 */

import { useState, useRef, useEffect } from 'react';
import { useCurrency } from '../context/CurrencyContext';
import { ChevronUp, ChevronDown, Check, Coins } from 'lucide-react';

export default function CurrencySelector({ className = '', compact = false, dropUp = false }) {
  const { currency, setCurrency, SUPPORTED_CURRENCIES, CURRENCY_CONFIG, loading } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm text-secondary-text ${className}`}>
        <span className="animate-pulse">...</span>
      </div>
    );
  }

  const currentConfig = CURRENCY_CONFIG[currency] || {};

  // Determine dropdown position classes
  const dropdownPositionClass = dropUp 
    ? 'bottom-full mb-1' 
    : 'top-full mt-1';

  // Match the LanguageSwitcher button style
  const buttonClass = compact 
    ? 'icon-btn w-10 h-10 flex items-center justify-center' 
    : 'icon-btn flex items-center gap-1.5 px-3 py-2';

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClass}
        aria-label="Select currency"
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {compact ? (
          <span className="text-sm font-semibold">{currency}</span>
        ) : (
          <>
            <span className="text-sm font-semibold">{currency}</span>
            {dropUp ? (
              <ChevronUp className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            ) : (
              <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            )}
          </>
        )}
      </button>

      {isOpen && (
        <div className={`absolute left-0 ${dropdownPositionClass} w-52 bg-card-bg border border-card-border rounded-xl shadow-lg z-50 overflow-hidden`}>
          <div className="p-2">
            <div className="text-xs font-medium text-secondary-text px-2 py-1.5 uppercase tracking-wide">
              Select Currency
            </div>
            {SUPPORTED_CURRENCIES.map((curr) => {
              const config = CURRENCY_CONFIG[curr] || {};
              const isSelected = curr === currency;

              return (
                <button
                  key={curr}
                  onClick={() => {
                    setCurrency(curr);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm transition-colors ${
                    isSelected
                      ? 'bg-primary/10 text-primary'
                      : 'text-primary-text hover:bg-subtle-bg'
                  }`}
                >
                  <span className="w-10 text-left font-semibold">{curr}</span>
                  <div className="flex-1 text-left">
                    <div className="text-xs text-secondary-text">{config.name}</div>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-primary" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Simple inline currency badge (for showing current currency)
 */
export function CurrencyBadge({ className = '' }) {
  const { currency, loading } = useCurrency();

  if (loading) return null;

  return (
    <span className={`inline-flex items-center gap-1 text-sm text-secondary-text ${className}`}>
      <Coins className="w-3 h-3" />
      <span>{currency}</span>
    </span>
  );
}
